<template>
    <v-card class="h-100">
        <v-row>
            <v-col>
                <v-btn icon-size="medium" variant="text" :to="`/teacher/marking`">
                    <template #prepend> <v-icon size="x-large">mdi-chevron-left</v-icon> </template> Go back
                </v-btn>
            </v-col>
            <v-col>
                <div class="d-flex justify-end gc-3 mt-auto">
                    <v-btn variant="outlined" color="brand" :to="`/teacher/task/${pupilTask?.task.id}/view-task`">
                        <template v-if="pupilTask" #prepend>
                            <TaskIcon :task="pupilTask?.task" />
                        </template>
                        {{ pupilTask?.task.title }}
                    </v-btn>
                    <v-btn
                        v-if="pupilTask && !pupilTask.task.aspire"
                        prepend-icon="mdi-printer"
                        color="brand"
                        variant="outlined"
                        @click="printPupilAnswers"
                    >
                        Print
                    </v-btn>
                    <template v-if="menuItems.length > 0">
                        <v-btn id="menu-activator" color="brand" variant="text" icon="mdi-dots-vertical" />
                        <v-menu activator="#menu-activator" class="extra-menu">
                            <v-list>
                                <v-list-item
                                    v-for="(item, index) in menuItems"
                                    :key="index"
                                    :value="index"
                                    @click="item.action"
                                >
                                    <template #prepend>
                                        <v-icon :icon="item.icon"></v-icon>
                                    </template>
                                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col style="flex-grow: 2">
                <v-card-title
                    ><h2>Mark {{ pupilName(pupilTask?.pupil) }}'s task</h2></v-card-title
                >
                <v-card-subtitle>
                    <p v-if="pupilTask?.status === 6" class="lead">
                        Review {{ pupilName(pupilTask?.pupil) }}'s archived answer{{
                            pupilTask.task.reading_test ? 's' : ''
                        }}
                    </p>
                    <p v-else class="lead">
                        Review {{ pupilName(pupilTask?.pupil) }}'s answer{{
                            pupilTask?.task.reading_test ? 's' : ' and provide feedback on this task'
                        }}.
                    </p>
                </v-card-subtitle>

                <v-card
                    v-if="!school?.bypass_marking && pupilTask?.status === 5 && pupilTask?.marked_by?.id"
                    class="mb-2"
                >
                    <h3>Marked By</h3>
                    <p v-if="teacher?.id === pupilTask?.marked_by.id">You marked this.</p>
                    <p v-else>{{ pupilTask.marked_by.name }} marked this.</p>
                </v-card>
            </v-col>
        </v-row>

        <v-card-text v-if="!school?.bypass_marking && pupilTask?.status !== 6" class="mb-2">
            <v-form id="mark_form" @submit.prevent="submitMarking">
                <v-row>
                    <template v-if="!pupilTask?.task.reading_test">
                        <v-col :cols="pupilTask?.task.hots && isAutomatedMarkingEnabled ? 6 : 12">
                            <v-textarea
                                id="teacher_feedback_general"
                                v-model="editedPupilTask.teacher_feedback_general"
                                label="General note to pupil"
                                :readonly="pupilTask && pupilTask.status >= 5"
                                name="teacher_feedback_general"
                                auto-grow
                                rows="1"
                                hide-details
                                prepend-inner-icon="mdi-pencil-outline"
                            />
                            <p v-if="pupilTask?.feedback_acknowledged">
                                The pupil acknowledged this feedback on
                                {{ formatDate(pupilTask.feedback_acknowledged) }}
                            </p>
                        </v-col>
                        <v-col v-if="pupilTask?.task.hots && isAutomatedMarkingEnabled" cols="6">
                            <v-skeleton-loader
                                :loading="isLoadingAutomatedMarking"
                                class="bg-surface-container-low mb-1"
                                type="paragraph"
                            >
                                <div
                                    v-if="feedback?.teacherFeedback"
                                    class="bg-surface-container-low rounded pa-3 w-100"
                                >
                                    <div class="d-flex w-100">
                                        <div class="text-grey mr-3">
                                            <v-icon icon="mdi-robot-outline"></v-icon>
                                            <v-tooltip activator="parent" width="300"
                                                >The feedback in this box has been generated with the use of Artificial
                                                Intelligence.
                                            </v-tooltip>
                                        </div>
                                        <div>
                                            <div class="mr-2">{{ feedback?.teacherFeedback }}</div>
                                            <v-chip
                                                v-for="part in [
                                                    'remember',
                                                    'understand',
                                                    'apply',
                                                    'analyse',
                                                    'evaluate',
                                                ]"
                                                :key="part"
                                                size="x-small"
                                                :color="`hots-${part}`"
                                                class="mt-1"
                                                >{{ capitalize(part) }}
                                                <v-icon
                                                    v-if="f(part)?.questionGrade === 'correct'"
                                                    icon="mdi-check"
                                                    color="success"
                                                    class="ml-1"
                                                ></v-icon>
                                                <v-icon
                                                    v-else-if="f(part)?.questionGrade === 'incorrect'"
                                                    icon="mdi-close"
                                                    color="error"
                                                    class="ml-1"
                                                ></v-icon>
                                                <v-icon v-else icon="mdi-minus" color="warning" class="ml-1"></v-icon>
                                            </v-chip>
                                        </div>
                                    </div>
                                </div>
                            </v-skeleton-loader>
                        </v-col>

                        <v-col :cols="pupilTask?.task.hots && isAutomatedMarkingEnabled ? 6 : 12">
                            <v-textarea
                                id="teacher_feedback_star1"
                                v-model="editedPupilTask.teacher_feedback_star1"
                                label="Something the pupil did well"
                                :readonly="pupilTask && pupilTask.status >= 5"
                                name="teacher_feedback_star1"
                                auto-grow
                                rows="1"
                                hide-details
                                prepend-inner-icon="mdi-star-outline"
                            />
                        </v-col>
                        <v-col v-if="pupilTask?.task.hots && isAutomatedMarkingEnabled" cols="6">
                            <v-skeleton-loader
                                :loading="isLoadingAutomatedMarking"
                                class="bg-surface-container-low mb-1"
                                type="paragraph"
                            >
                                <div
                                    v-if="feedback?.positiveFeedback[0]"
                                    class="bg-surface-container-low rounded pa-3 d-flex w-100"
                                >
                                    <div class="d-flex w-100">
                                        <div class="text-grey mr-3">
                                            <v-icon icon="mdi-robot-outline"></v-icon>
                                            <v-tooltip activator="parent" width="300"
                                                >The feedback in this box has been generated with the use of Artificial
                                                Intelligence.
                                            </v-tooltip>
                                        </div>
                                        <div class="mr-2">{{ feedback?.positiveFeedback[0] }}</div>
                                    </div>
                                    <a
                                        v-if="editable"
                                        class="text-caption text-decoration-none text-no-wrap"
                                        @click="useThis(feedback?.positiveFeedback[0], 'teacher_feedback_star1')"
                                        >Use This</a
                                    >
                                </div>
                            </v-skeleton-loader>
                        </v-col>

                        <v-col :cols="pupilTask?.task.hots && isAutomatedMarkingEnabled ? 6 : 12">
                            <v-textarea
                                id="teacher_feedback_star2"
                                v-model="editedPupilTask.teacher_feedback_star2"
                                label="Another thing the pupil did well"
                                :readonly="pupilTask && pupilTask.status >= 5"
                                name="teacher_feedback_star2"
                                auto-grow
                                rows="1"
                                hide-details
                                prepend-inner-icon="mdi-star-outline"
                            />
                        </v-col>
                        <v-col v-if="pupilTask?.task.hots && isAutomatedMarkingEnabled" cols="6">
                            <v-skeleton-loader
                                :loading="isLoadingAutomatedMarking"
                                class="bg-surface-container-low mb-1"
                                type="paragraph"
                            >
                                <div
                                    v-if="feedback?.positiveFeedback[1]"
                                    class="bg-surface-container-low rounded pa-3 d-flex w-100"
                                >
                                    <div class="d-flex w-100">
                                        <div class="text-grey mr-3">
                                            <v-icon icon="mdi-robot-outline"></v-icon>
                                            <v-tooltip activator="parent" width="300"
                                                >The feedback in this box has been generated with the use of Artificial
                                                Intelligence.
                                            </v-tooltip>
                                        </div>
                                        <div class="mr-2">{{ feedback?.positiveFeedback[1] }}</div>
                                    </div>
                                    <a
                                        v-if="editable"
                                        class="text-caption text-decoration-none text-no-wrap"
                                        @click="useThis(feedback?.positiveFeedback[1], 'teacher_feedback_star2')"
                                        >Use This</a
                                    >
                                </div>
                            </v-skeleton-loader>
                        </v-col>

                        <v-col :cols="pupilTask?.task.hots && isAutomatedMarkingEnabled ? 6 : 12">
                            <v-textarea
                                id="teacher_feedback_wish"
                                v-model="editedPupilTask.teacher_feedback_wish"
                                :readonly="pupilTask && pupilTask.status >= 5"
                                label="Where the pupil can improve"
                                name="teacher_feedback_wish"
                                auto-grow
                                rows="1"
                                hide-details
                                prepend-inner-icon="mdi-exclamation"
                            />
                        </v-col>
                        <v-col v-if="pupilTask?.task.hots && isAutomatedMarkingEnabled" cols="6">
                            <v-skeleton-loader
                                :loading="isLoadingAutomatedMarking"
                                class="bg-surface-container-low mb-1"
                                type="paragraph"
                            >
                                <div
                                    v-if="feedback?.constructiveFeedback"
                                    class="bg-surface-container-low rounded pa-3 d-flex w-100"
                                >
                                    <div class="d-flex w-100">
                                        <div class="text-grey mr-3">
                                            <v-icon icon="mdi-robot-outline"></v-icon>
                                            <v-tooltip activator="parent" width="300"
                                                >The feedback in this box has been generated with the use of Artificial
                                                Intelligence.
                                            </v-tooltip>
                                        </div>
                                        <div class="mr-2">{{ feedback?.constructiveFeedback }}</div>
                                    </div>
                                    <a
                                        v-if="editable"
                                        class="float-right text-caption text-decoration-none text-no-wrap"
                                        @click="useThis(feedback?.constructiveFeedback, 'teacher_feedback_wish')"
                                        >Use This</a
                                    >
                                </div>
                            </v-skeleton-loader>
                        </v-col>
                    </template>

                    <v-col cols="12">
                        <v-textarea
                            id="teacher_feedback_note"
                            v-model="editedPupilTask.teacher_feedback_note"
                            label="Teacher's notes (not visible to pupil)"
                            :readonly="pupilTask && pupilTask.status >= 5"
                            name="teacher_feedback_note"
                            auto-grow
                            rows="1"
                            hide-details
                            prepend-inner-icon="mdi-lock-outline"
                        ></v-textarea>
                    </v-col>
                    <v-col v-if="editable" cols="12">
                        <div class="d-flex">
                            <v-btn type="submit" variant="flat" color="brand">Save</v-btn>
                            <v-checkbox
                                v-model="setComplete"
                                class="ml-5"
                                name="complete"
                                label="Yes, this task is complete"
                                hide-details
                                density="compact"
                            />
                        </div>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <div class="grid">
            <div class="unit whole">
                <div class="grid">
                    <template v-if="pupilTask?.task.reading_test && pupilTask.status && pupilTask.status > 1">
                        <div class="panel">
                            <h2>Overview</h2>
                            <div class="grid marking-grid">
                                <div class="unit one-tenth">
                                    <h3>Step</h3>
                                </div>
                                <div class="unit three-twentieths">
                                    <h3>Correct</h3>
                                </div>
                                <div :class="['unit', showAttemptsColumn ? 'three-fifths' : 'three-quarters']">
                                    <h3>Type</h3>
                                </div>

                                <div v-if="showAttemptsColumn" class="unit three-twentieths">
                                    <h3>Attempts</h3>
                                </div>

                                <template v-for="(ps, idx) in sortedPupilSteps" :key="ps.id">
                                    <div class="unit one-tenth task_step_link">
                                        <p>
                                            <a @click="goToStep(idx)">{{ idx + 1 }}</a>
                                        </p>
                                    </div>

                                    <div class="unit three-twentieths">
                                        <p>
                                            <i v-if="ps.correct" class="fa fa-fw fa-check marking-icon"></i>
                                            <i
                                                v-else-if="nonMarkableStepTypes.includes(ps.step.step_type.code)"
                                                class="fa fa-fw fa-minus marking-icon"
                                            ></i>
                                            <i v-else class="fa fa-fw fa-close marking-icon"></i>
                                        </p>
                                    </div>

                                    <div :class="['unit', showAttemptsColumn ? 'three-fifths' : 'three-quarters']">
                                        <p>{{ ps.step.step_type.name }}</p>
                                    </div>

                                    <div v-if="showAttemptsColumn" class="unit three-twentieths">
                                        <p v-if="ps.attempts">{{ ps.attempts ?? '-' }}</p>
                                        <p v-else><i class="fa fa-fw fa-minus marking-icon"></i></p>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>

                    <template v-if="pupilTask?.task.hots">
                        <Hots
                            :pupilTask="pupilTask"
                            :answers="answers"
                            :feedback="feedback"
                            :isLoadingAutomatedMarking="isLoadingAutomatedMarking"
                            :isLoadingAutomatedAnswers="isLoadingAutomatedAnswers"
                            :isAutomatedMarkingEnabled="isAutomatedMarkingEnabled"
                        />
                    </template>
                    <div v-else-if="pupilTask?.task.aspire" class="panel">
                        <Aspire :pupilTask="pupilTask" />
                    </div>
                    <div v-else-if="pupilTask?.pupil_steps?.length" id="task_steps" class="panel">
                        <TaskStepAnswers ref="taskStepAnswersRef" :pupil-steps="sortedPupilSteps" />
                    </div>
                    <div v-else class="panel">
                        <TaskStepAnswers :pupil-steps="[]" :task-steps="pupilTask?.task.steps" />
                    </div>

                    <v-card v-if="!pupilTask?.task.reading_test && !school?.bypass_marking">
                        <v-card-title>{{ pupilName(pupilTask?.pupil) }}'s self evaluation</v-card-title>
                        <v-card-text>
                            <template v-if="pupilTask?.status && pupilTask.status > 1">
                                <v-label>Confidence:</v-label>
                                <p>{{ pupilTask.confidence && confidenceText(pupilTask.confidence) }}</p>
                                <v-label class="mt-2">{{
                                    pupilTask?.feedback_star1
                                        ? pupilTask.feedback_star1
                                        : 'One thing you think you did well:'
                                }}</v-label>
                                <p>{{ pupilTask.pupil_feedback_star1 }}</p>
                                <v-label class="mt-2">{{
                                    pupilTask?.feedback_star2
                                        ? pupilTask.feedback_star2
                                        : 'A second thing you think you did well:'
                                }}</v-label>
                                <p>{{ pupilTask.pupil_feedback_star2 }}</p>
                                <v-label class="mt-2">
                                    {{
                                        pupilTask?.feedback_wish
                                            ? pupilTask.feedback_wish
                                            : 'One thing you think you could improve on next time:'
                                    }}
                                </v-label>
                                <p>{{ pupilTask.pupil_feedback_wish }}</p>
                            </template>
                            <template v-else>
                                <p>No pupil evaluation submitted yet</p>
                            </template>
                        </v-card-text>
                    </v-card>
                </div>
            </div>
        </div>
    </v-card>
    <!-- Hidden print container -->
    <div v-if="pupilTask && !pupilTask.task.aspire" id="js_print" class="d-none">
        <PrintHOTS
            v-if="pupilTask.task.hots"
            :task="pupilTask.task"
            :school-name="school?.name || ''"
            :show-answers="false"
            :pupil-task="pupilTask"
            :pupil-name="pupilName(pupilTask.pupil)"
        />
        <PrintNonHOTS
            v-else
            :task="pupilTask.task"
            :school-name="school?.name || ''"
            :show-answers="false"
            :pupil-task="pupilTask"
            :pupil-name="pupilName(pupilTask.pupil)"
        />
    </div>
    <v-overlay
        :model-value="isLoadingOrSaving"
        height="100vh"
        width="100vw"
        content-class="d-flex justify-center align-center"
    >
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-dialog v-model="showReassignDialog" max-width="500">
        <v-card title="Re-assign task">
            <v-card-text>
                <v-textarea v-model="reassignComment" placeholder="Re-assign comments" name="reassign_comment" />
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="flat" color="brand" @click="handleReassign">Re-assign task</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { HotsTaskAnswers, HotsTaskFeedback } from '@ilteducation/giglets-task-feedback-types';
import { useTeacherStore } from '~/stores/teacher';
import TaskStepAnswers from '~/components/teacher/TaskStepAnswers.vue';
import Aspire from '~/components/teacher/mark/Aspire.vue';
import Hots from '~/components/teacher/mark/Hots.vue';
import PrintHOTS from '~/components/teacher/print/PrintHOTS.vue';
import PrintNonHOTS from '~/components/teacher/print/PrintNonHOTS.vue';
import print from '@/utils/print';
import { PupilTask } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'marking' } });
const route = useRoute('/teacher/mark/[[pupil_task_id]]');
const pupilTaskId = ref(route.params.pupil_task_id + '');
const isLoadingOrSaving = ref(true);

const teacherStore = useTeacherStore();
const { fetchTeacher, fetchPupils, fetchPupilTasks, fetchSchool, updatePupilTask, reassignTask, pupilName } =
    teacherStore;
fetchTeacher();
fetchPupils();
fetchPupilTasks();
fetchSchool();
const { pupilTasks, pupils, school, teacher } = storeToRefs(teacherStore);
const { formatDate } = useDateFormat();
const router = useRouter();
const { capitalize } = useFilters();

const nonMarkableStepTypes = ['text', 'offline', 'instruction'];
const teacherHasPupilInAnyClass = computed(
    () => pupilTask.value && pupils.value.map((p) => p.id).includes(pupilTask.value.pupil.id),
);
const pupilTask = computed(() => pupilTasks.value.find((t) => t.id.toString() === pupilTaskId.value));
const sortedPupilSteps = computed(
    () => pupilTask.value?.pupil_steps?.toSorted((a, b) => a.step.position - b.step.position) || [],
);
const showAttemptsColumn = computed(() => pupilTask.value?.pupil_steps?.some((ps) => ps.attempts > 0));
const taskStepAnswersRef = ref<InstanceType<typeof TaskStepAnswers>>();
const showReassignDialog = ref(false);

const editable = computed(() => pupilTask.value && pupilTask.value.status < 5);

const goToStep = (stepIndex: number) => {
    taskStepAnswersRef.value?.setStepIndex(stepIndex);
};

const confidenceText = (confidence: number) => {
    switch (confidence) {
        case 1:
            return 'Confident';
        case 2:
            return 'Not sure';
        case 3:
            return "Don't understand";
        default:
            return '';
    }
};

const reassignComment = ref('');

const setComplete = ref(false);

const editedPupilTask = ref<PupilTask>({} as PupilTask);
watch(
    pupilTask,
    (pt) => {
        if (pt && pt.status > 1) {
            setComplete.value = true;
        }
        if (pt) {
            editedPupilTask.value = { ...pt };
            isLoadingOrSaving.value = false;
        }
    },
    { immediate: true },
);

const answers = ref<HotsTaskAnswers>();
const feedback = ref<HotsTaskFeedback>();
const isLoadingAutomatedAnswers = ref(false);
const isLoadingAutomatedMarking = ref(false);
const automatedMarking = useAutomatedMarking();
const { getAutomatedAnswersForHotsTask, getAutomatedFeedbackForHotsTask } = automatedMarking;
const { isAutomatedMarkingEnabled } = storeToRefs(automatedMarking);

watch(
    pupilTask,
    async (pt) => {
        if (!pt || !pt.task.hots) {
            return;
        }
        isLoadingAutomatedAnswers.value = true;
        try {
            answers.value = await getAutomatedAnswersForHotsTask(pt.task);
        } finally {
            isLoadingAutomatedAnswers.value = false;
        }
    },
    { immediate: true },
);

watch(
    pupilTask,
    async (pt) => {
        if (!pt || !pt.task.hots || !isAutomatedMarkingEnabled.value || pt.status < 2) {
            return;
        }

        isLoadingAutomatedMarking.value = true;
        try {
            feedback.value = await getAutomatedFeedbackForHotsTask(pt).catch((e) => {
                if (e.response.status === 404) {
                    return undefined;
                }
                throw e;
            });
        } finally {
            isLoadingAutomatedMarking.value = false;
        }
    },
    { immediate: true },
);

const handleReassign = async () => {
    isLoadingOrSaving.value = true;
    await reassignTask(parseInt(pupilTaskId.value), reassignComment.value);
    await router.push('/teacher/marking?status=submitted');
};

type MenuItem = {
    title: string;
    icon: string;
    action: () => void;
};

const menuItems = ref<MenuItem[]>([]);
watchEffect(() => {
    if ((pupilTask.value?.task.hots || pupilTask.value?.task.reading_test) && teacherHasPupilInAnyClass) {
        menuItems.value.push({
            title: 'Re-assign task',
            icon: 'mdi-reload',
            action: () => (showReassignDialog.value = true),
        });
    }
});

const submitMarking = async () => {
    isLoadingOrSaving.value = true;
    const updatePayload: Partial<PupilTask> = {};

    if (editedPupilTask.value.teacher_feedback_star1 !== pupilTask.value?.teacher_feedback_star1) {
        updatePayload.teacher_feedback_star1 = editedPupilTask.value.teacher_feedback_star1;
    }
    if (editedPupilTask.value.teacher_feedback_star2 !== pupilTask.value?.teacher_feedback_star2) {
        updatePayload.teacher_feedback_star2 = editedPupilTask.value.teacher_feedback_star2;
    }
    if (editedPupilTask.value.teacher_feedback_wish !== pupilTask.value?.teacher_feedback_wish) {
        updatePayload.teacher_feedback_wish = editedPupilTask.value.teacher_feedback_wish;
    }
    if (editedPupilTask.value.teacher_feedback_general !== pupilTask.value?.teacher_feedback_general) {
        updatePayload.teacher_feedback_general = editedPupilTask.value.teacher_feedback_general;
    }
    if (editedPupilTask.value.teacher_feedback_note !== pupilTask.value?.teacher_feedback_note) {
        updatePayload.teacher_feedback_note = editedPupilTask.value.teacher_feedback_note;
    }

    if (setComplete.value) {
        updatePayload.status = 5;
    }
    await updatePupilTask(parseInt(pupilTaskId.value), updatePayload);
    router.push('/teacher/marking?status=submitted');
};

function printPupilAnswers() {
    nextTick(() => print('js_print'));
}

function useThis(
    text: string,
    field: 'teacher_feedback_star1' | 'teacher_feedback_star2' | 'teacher_feedback_wish' | 'teacher_feedback_general',
) {
    editedPupilTask.value[field] = text;
}

const f = (part: string) => {
    return feedback.value?.questionsFeedback.find((q) => q.questionPart === capitalize(part));
};
</script>

<style scoped lang="scss">
:deep(form) textarea {
    margin: revert;
}
</style>
